"use client";

import { HTMLAttributes, useId, useState } from "react";
import Typography from "../typography";
import { Heading } from "../typography/Heading";
import Link from "next/link";
import Image from "next/image";

type CollapsibleData = {
  question: string;
  answer: string;
};

type CollapsibleProps = HTMLAttributes<HTMLDivElement> & {
  items: CollapsibleData[];
};

function Collapsible({ items, ...props }: CollapsibleProps) {
  const uid = useId();
  return (
    <div {...props}>
      {items.map((item, index) => (
        <div
          key={`collapsible-${uid}-${index}`}
          className="px-5 pt-4 bg-white rounded-lg w-full group"
        >
          <input
            id={`collapsible-${uid}-${index}`}
            type="checkbox"
            className="peer hidden"
          />
          <label
            htmlFor={`collapsible-${uid}-${index}`}
            className="flex flex-row cursor-pointer pb-4"
          >
            <Typography
              as="h3"
              className="flex-5 self-center"
              size="body1"
              dangerouslySetInnerHTML={{ __html: item.question }}
            />
            <div className="grow" />
            <svg
              className="float-right transition-transform duration-300 rotate-180 group-has-[:checked]:rotate-0"
              width="30"
              height="30"
              viewBox="0 0 30 30"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                y="30"
                width="30"
                height="30"
                rx="15"
                transform="rotate(-90 0 30)"
                fill="#F5F5F7"
              />
              <path
                d="M15.4243 9.58355C15.1899 9.34923 14.81 9.34923 14.5757 9.58355L10.7574 13.4019C10.523 13.6362 10.523 14.0161 10.7574 14.2505C10.9917 14.4848 11.3716 14.4848 11.6059 14.2505L15 10.8563L18.3941 14.2505C18.6284 14.4848 19.0083 14.4848 19.2426 14.2505C19.477 14.0161 19.477 13.6362 19.2426 13.4019L15.4243 9.58355ZM14.4 20.5078C14.4 20.8392 14.6686 21.1078 15 21.1078C15.3314 21.1078 15.6 20.8392 15.6 20.5078L14.4 20.5078ZM14.4 10.0078L14.4 20.5078L15.6 20.5078L15.6 10.0078L14.4 10.0078Z"
                fill="#222429"
              />
            </svg>
          </label>

          <div
            role="region"
            id={`section-${uid}-${index + 1}`}
            className="grid transition-[grid-template-rows] duration-300 ease-in-out grid-rows-[0fr] peer-checked:grid-rows-[1fr]"
          >
            <div className="overflow-hidden">
              <Typography
                size="body2"
                color="copyGray"
                className="pb-4"
                dangerouslySetInnerHTML={{ __html: item.answer }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

interface TypeofCarsCollapsibleProps {
  items: Array<{
    title: string;
    list_type: string;
    description?: string;
    list?: Array<{
      item: string;
      link?: string;
    }>;
    footer?: Array<{
      title: string;
      description?: string;
    }>;
  }>;
  className?: string;
}

function TypeofCarsCollapsible({
  items,

  ...props
}: TypeofCarsCollapsibleProps) {
  const [openItemIndex, setOpenItemIndex] = useState<number | null>(null);

  const toggleItem = (index: number) => {
    setOpenItemIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  return (
    <div {...props} className={`flex flex-col gap-y-15px`}>
      {items.map((item, index) => (
        <article
          key={index}
          className={`px-5 pt-18px ${
            openItemIndex != index ? "pb-18px" : "pb-0"
          } bg-bgColor rounded-lg justify-center items-center w-full`}
        >
          <header
            onClick={() => toggleItem(index)}
            className="flex flex-row cursor-pointer"
          >
            <Heading
              as="h4"
              className="flex-5 self-center"
              size="exotic_h5_body2"
              dangerouslySetInnerHTML={{ __html: item.title }}
            />
            <div className="grow" />
            <button
              aria-label={`toggle-section-${index + 1}`}
              aria-controls={`section-${index + 1}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleItem(index);
              }}
            >
              <svg
                className={`float-right cursor-pointer transition-transform duration-300 ${
                  openItemIndex === index ? "rotate-0" : "rotate-180"
                }`}
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  y="30"
                  width="30"
                  height="30"
                  rx="15"
                  transform="rotate(-90 0 30)"
                  fill="#F5F5F7"
                />
                <path
                  d="M15.4243 9.58355C15.1899 9.34923 14.81 9.34923 14.5757 9.58355L10.7574 13.4019C10.523 13.6362 10.523 14.0161 10.7574 14.2505C10.9917 14.4848 11.3716 14.4848 11.6059 14.2505L15 10.8563L18.3941 14.2505C18.6284 14.4848 19.0083 14.4848 19.2426 14.2505C19.477 14.0161 19.477 13.6362 19.2426 13.4019L15.4243 9.58355ZM14.4 20.5078C14.4 20.8392 14.6686 21.1078 15 21.1078C15.3314 21.1078 15.6 20.8392 15.6 20.5078L14.4 20.5078ZM14.4 10.0078L14.4 20.5078L15.6 20.5078L15.6 10.0078L14.4 10.0078Z"
                  fill="#222429"
                />
              </svg>
            </button>
          </header>
          <div
            role="region"
            className={`transition-all ${
              openItemIndex != index
                ? "max-h-0 opacity-0 overflow-y-hidden py-0"
                : "py-4"
            } `}
            id={`section-${index + 1}`}
          >
            <Typography
              size="body1"
              dangerouslySetInnerHTML={{ __html: item.description || "" }}
            ></Typography>
            {item.list && item.list.length > 0 && (
              <ul
                className={`${
                  item.list_type === "checkmarks"
                    ? "grid-cols-2 grid"
                    : "list-disc list-outside pl-5"
                }   mt-2`}
              >
                {item.list.map((listItem, listIndex) => (
                  <li
                    key={listIndex}
                    className={`${
                      item.list_type === "checkmarks"
                        ? "pui-list-item-with-check-icon"
                        : ""
                    } mb-1`}
                  >
                    {listItem.link ? (
                      <Link
                        href={listItem.link}
                        className="hover:underline underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Typography size="body1">{listItem.item}</Typography>
                      </Link>
                    ) : (
                      <Typography size="body1">{listItem.item}</Typography>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {item.footer && item.footer.length > 0 && (
              <footer className="border-t border-howerGray pt-2.5 mt-5">
                {item.footer.map((footerItem, footerIndex) => (
                  <div key={footerIndex} className="flex blogContainer">
                    <Typography
                      className="flex"
                      key={footerIndex}
                      dangerouslySetInnerHTML={{
                        __html: footerItem.title,
                      }}
                      size="body1"
                    ></Typography>

                    <Typography
                      className="flex"
                      key={footerIndex}
                      dangerouslySetInnerHTML={{
                        __html: footerItem.description!,
                      }}
                      size="body1"
                    ></Typography>
                  </div>
                ))}
              </footer>
            )}
          </div>
        </article>
      ))}
    </div>
  );
}

interface CarDetailingCollapsibleProps extends HTMLAttributes<HTMLDivElement> {
  items?: Array<{
    title: string;
    description: string;
    subheader?: string;
    subdescription?: string;
  }>;
}

//add HTMLDivelement

function CarDetailingCollapsible({
  items,
  ...props
}: CarDetailingCollapsibleProps) {
  const [openItemIndex, setOpenItemIndex] = useState<number | null>(null);

  const toggleItem = (index: number) => {
    setOpenItemIndex((prevIndex) => (prevIndex === index ? null : index));
  };
  return (
    <div {...props}>
      {items?.map((item, index) => (
        <article
          key={index}
          className={`px-5 pt-4 ${
            openItemIndex != index ? "pb-4" : "pb-0"
          } bg-bgColor rounded-lg justify-center items-center w-full`}
        >
          <header
            onClick={() => toggleItem(index)}
            className="flex flex-row cursor-pointer"
          >
            <Typography
              as="h4"
              className="flex-5 self-center"
              size="unresponsiveBody1"
              dangerouslySetInnerHTML={{ __html: item.title }}
            />
            <div className="grow" />
            <button
              aria-label={`toggle-section-${index + 1}`}
              aria-controls={`section-${index + 1}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleItem(index);
              }}
            >
              <svg
                className={`float-right cursor-pointer transition-transform duration-300 ${
                  openItemIndex === index ? "rotate-0" : "rotate-180"
                }`}
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  y="30"
                  width="30"
                  height="30"
                  rx="15"
                  transform="rotate(-90 0 30)"
                  fill="#F5F5F7"
                />
                <path
                  d="M15.4243 9.58355C15.1899 9.34923 14.81 9.34923 14.5757 9.58355L10.7574 13.4019C10.523 13.6362 10.523 14.0161 10.7574 14.2505C10.9917 14.4848 11.3716 14.4848 11.6059 14.2505L15 10.8563L18.3941 14.2505C18.6284 14.4848 19.0083 14.4848 19.2426 14.2505C19.477 14.0161 19.477 13.6362 19.2426 13.4019L15.4243 9.58355ZM14.4 20.5078C14.4 20.8392 14.6686 21.1078 15 21.1078C15.3314 21.1078 15.6 20.8392 15.6 20.5078L14.4 20.5078ZM14.4 10.0078L14.4 20.5078L15.6 20.5078L15.6 10.0078L14.4 10.0078Z"
                  fill="#222429"
                />
              </svg>
            </button>
          </header>
          <div
            role="region"
            className={`transition-all ${
              openItemIndex != index
                ? "max-h-0 opacity-0 overflow-y-hidden py-0"
                : "py-4"
            } `}
            id={`section-${index + 1}`}
          >
            <Typography
              size="unresponsiveBody2"
              color="copyGray"
              dangerouslySetInnerHTML={{ __html: item.description }}
            ></Typography>

            {item.subheader && (
              <Typography
                className="lg:mt-5 mt-15px"
                as="h5"
                size="unresponsiveBody1"
                weight={400}
                dangerouslySetInnerHTML={{ __html: item.subheader }}
              ></Typography>
            )}

            {item.subdescription && (
              <Typography
                size="unresponsiveBody2"
                color="copyGray"
                dangerouslySetInnerHTML={{ __html: item.subdescription }}
              ></Typography>
            )}
          </div>
        </article>
      ))}
    </div>
  );
}

interface LpCollapsibleProps extends HTMLAttributes<HTMLDivElement> {
  items?: Array<{
    title: string;
    description: string;
  }>;
}

//add HTMLDivelement

function LpCollapsible({ items, ...props }: LpCollapsibleProps) {
  const [openItemIndex, setOpenItemIndex] = useState<number | null>(null);

  const toggleItem = (index: number) => {
    setOpenItemIndex((prevIndex) => (prevIndex === index ? null : index));
  };
  return (
    <div {...props}>
      {items?.map((item, index) => (
        <article
          key={index}
          className={`px-5 pt-4 ${
            openItemIndex != index ? "pb-4" : "pb-0"
          } bg-blue-50 rounded-lg justify-center items-center w-full`}
        >
          <header
            onClick={() => toggleItem(index)}
            className="flex flex-row cursor-pointer"
          >
            <h3
              className="flex-5 self-stretch justify-start text-black text-base font-medium  leading-6"
              dangerouslySetInnerHTML={{ __html: item.title }}
            />
            <div className="grow" />
            <button
              aria-label={`toggle-section-${index + 1}`}
              aria-controls={`section-${index + 1}`}
              className="rounded-full "
              onClick={(e) => {
                e.stopPropagation();
                toggleItem(index);
              }}
            >
              <Image
                src={
                  openItemIndex === index
                    ? "9610160e-4e2f-4872-83f7-a0e1313541c4.svg"
                    : "7da38ee5-8385-4730-841d-51d5b5db56d9.svg"
                }
                height={24}
                width={24}
                className="shrink-0"
                alt=""
              />
            </button>
          </header>
          <div
            role="region"
            className={`transition-all ${
              openItemIndex != index
                ? "max-h-0 opacity-0 overflow-y-hidden py-0"
                : "py-4"
            } `}
            id={`section-${index + 1}`}
          >
            <p
              color="copyGray"
              className="self-stretch justify-start text-black text-base font-normal  leading-6"
              dangerouslySetInnerHTML={{ __html: item.description }}
            ></p>
          </div>
        </article>
      ))}
    </div>
  );
}

export {
  Collapsible,
  CarDetailingCollapsible,
  TypeofCarsCollapsible,
  LpCollapsible,
};
